/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React from 'react';
import { ShieldCheck, FileText, Download, Eye, Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

export const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString('it-IT', { dateStyle: 'medium', timeStyle: 'short' }) : '—');
export const fmtSize = (b) => (b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round((b || 0) / 1024))} KB`);
export const CATEGORY_LABEL = { referto: 'Referto', ricetta: 'Ricetta', lettera: 'Lettera di dimissione', esame: 'Esame di laboratorio', altro: 'Documento' };

export const E2EBadge = () => (
  <Badge variant="outline" className="gap-1 text-[10px] border-primary/40 text-primary" data-testid="portal-e2e-badge">
    <ShieldCheck className="h-3 w-3" /> Cifratura end-to-end · il server non può leggere
  </Badge>
);

const ReportRow = ({ r, highlighted, decrypting, onOpen }) => (
  <li className={cn('rounded-xl border p-3 flex items-center gap-3 flex-wrap', highlighted ? 'border-primary bg-primary/5 ring-2 ring-primary/30' : 'border-border/70')} data-testid={`portal-report-${r.id}`}>
    <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0"><FileText className="h-5 w-5" /></div>
    <div className="flex-1 min-w-[180px]">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-semibold text-sm">{r.title}</span>
        <Badge variant="outline" className="text-[10px]">{CATEGORY_LABEL[r.category] || r.category}</Badge>
        {!r.opened_at && <Badge variant="success" className="text-[10px]">nuovo</Badge>}
        {r.approved_at && <Badge variant="outline" className="text-[10px] border-success/50 text-success" data-testid={`portal-report-approved-${r.id}`}>validato dal medico</Badge>}
      </div>
      <div className="text-[11px] text-muted-foreground">{r.author_name || 'Professionista'} · {fmtDate(r.created_at)} · {fmtSize(r.size)} · <span className="font-mono">{(r.sha256 || '').slice(0, 10)}</span></div>
      {r.note && <div className="text-xs mt-1">{r.note}</div>}
    </div>
    <div className="flex gap-2">
      <Button size="sm" onClick={() => onOpen(r, 'view')} disabled={decrypting} className="rounded-xl h-9" data-testid={`portal-report-view-${r.id}`}>{decrypting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />} Apri</Button>
      <Button size="sm" variant="outline" onClick={() => onOpen(r, 'download')} disabled={decrypting} className="rounded-xl h-9" data-testid={`portal-report-download-${r.id}`}><Download className="h-4 w-4" /></Button>
    </div>
  </li>
);

/** Elenco referti cifrati (skeleton / vuoto / lista). */
export const ReportsList = ({ reports, loading, highlightId, decrypting, onOpen }) => {
  if (loading && reports.length === 0) {
    return <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-16 rounded-xl" />)}</div>;
  }
  if (reports.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground" data-testid="portal-reports-empty">
        <ShieldCheck className="h-8 w-8 mx-auto mb-2 text-primary/60" />
        Nessun documento ancora. Quando il tuo medico invierà un referto cifrato, lo troverai qui.
      </div>
    );
  }
  return (
    <ul className="space-y-2" data-testid="portal-reports-list">
      {reports.map((r) => <ReportRow key={r.id} r={r} highlighted={r.id === highlightId} decrypting={decrypting === r.id} onOpen={onOpen} />)}
    </ul>
  );
};

const saveViewer = (viewer) => {
  const a = document.createElement('a');
  a.href = viewer.url; a.download = viewer.meta.filename || 'referto.pdf'; a.click();
};

/** Visualizzatore del referto decifrato in memoria (immagine o PDF). */
export const ReportViewerDialog = ({ viewer, onClose }) => {
  const isImage = viewer && (viewer.meta.mime || '').startsWith('image/');
  return (
    <Dialog open={!!viewer} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl h-[85vh] flex flex-col rounded-xl" data-testid="portal-viewer">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-success" /> {viewer?.meta?.title}</DialogTitle>
          <DialogDescription>Decifrato localmente · SHA-256 verificato · <span className="font-mono">{(viewer?.meta?.sha256 || '').slice(0, 20)}…</span></DialogDescription>
        </DialogHeader>
        {viewer && isImage && <img src={viewer.url} alt={viewer.meta.title} className="flex-1 object-contain min-h-0 rounded-xl" />}
        {viewer && !isImage && (
          <object data={viewer.url} type={viewer.meta.mime || 'application/pdf'} className="flex-1 w-full rounded-xl border border-border/70 bg-muted/30" aria-label="Referto decifrato">
            <div className="h-full flex flex-col items-center justify-center gap-2 p-6 text-center text-sm text-muted-foreground" data-testid="portal-viewer-fallback">
              <FileText className="h-8 w-8 text-primary/60" />
              Il browser non mostra i PDF in anteprima: usa <b>Salva sul dispositivo</b> per aprirlo. Il file è già decifrato in memoria.
            </div>
          </object>
        )}
        {viewer && <div className="flex justify-end"><Button onClick={() => saveViewer(viewer)} className="rounded-xl h-10" data-testid="portal-viewer-download"><Download className="h-4 w-4" /> Salva sul dispositivo</Button></div>}
      </DialogContent>
    </Dialog>
  );
};
